import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import sprayBottle from "@/assets/spray-bottle.png";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col bg-brand-gradient-tr overflow-hidden relative px-12 py-12">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute bottom-0 -left-16 w-80 h-80 rounded-full bg-white/10 blur-3xl" />

        {/* Large spray bottle with sparkle + spray mist */}
        <div className="relative flex-1 flex items-center justify-center pointer-events-none" aria-hidden="true">
          <div className="relative animate-float-slow">
            {/* soft (subtle) glow behind the bottle */}
            <div className="absolute inset-0 -z-10 blur-3xl rounded-full bg-white/15 scale-75" />

            <img
              src={sprayBottle}
              alt=""
              className="h-[62vh] max-h-[640px] w-auto object-contain scale-x-[-1] drop-shadow-[0_0_14px_rgba(255,255,255,0.3)]"
            />

            {/* spray mist emitting from the nozzle tip (top-right after flip) */}
            <svg
              viewBox="0 0 110 90"
              className="absolute w-[34%] left-[76%] top-[15%] -translate-y-1/2 scale-x-[-1]"
              fill="none"
              stroke="#FFFFFF"
              strokeWidth="2.5"
              strokeLinecap="round"
            >
              <g className="animate-mist" style={{ transformOrigin: "100% 45px" }}>
                {[
                  { cx: 93, cy: 45, r: 1.2, o: 0.95 }, { cx: 89, cy: 41, r: 1.0, o: 0.9 }, { cx: 89, cy: 49, r: 1.0, o: 0.9 },
                  { cx: 82, cy: 44, r: 1.3, o: 0.85 }, { cx: 80, cy: 38, r: 1.0, o: 0.8 }, { cx: 80, cy: 52, r: 1.0, o: 0.8 },
                  { cx: 72, cy: 46, r: 1.4, o: 0.8 }, { cx: 70, cy: 34, r: 1.1, o: 0.72 }, { cx: 70, cy: 57, r: 1.1, o: 0.72 },
                  { cx: 62, cy: 40, r: 1.2, o: 0.68 }, { cx: 62, cy: 51, r: 1.2, o: 0.68 }, { cx: 60, cy: 29, r: 0.9, o: 0.6 },
                  { cx: 60, cy: 62, r: 0.9, o: 0.6 }, { cx: 50, cy: 45, r: 1.3, o: 0.58 }, { cx: 50, cy: 36, r: 1.0, o: 0.52 },
                  { cx: 50, cy: 55, r: 1.0, o: 0.52 }, { cx: 48, cy: 25, r: 0.8, o: 0.45 }, { cx: 48, cy: 66, r: 0.8, o: 0.45 },
                  { cx: 38, cy: 41, r: 0.95, o: 0.42 }, { cx: 38, cy: 52, r: 0.95, o: 0.42 }, { cx: 36, cy: 31, r: 0.75, o: 0.36 },
                  { cx: 36, cy: 61, r: 0.75, o: 0.36 }, { cx: 28, cy: 46, r: 0.85, o: 0.32 }, { cx: 26, cy: 36, r: 0.65, o: 0.28 },
                  { cx: 26, cy: 57, r: 0.65, o: 0.28 }, { cx: 18, cy: 44, r: 0.7, o: 0.24 }, { cx: 16, cy: 51, r: 0.55, o: 0.2 },
                  { cx: 12, cy: 39, r: 0.55, o: 0.18 }, { cx: 9, cy: 47, r: 0.5, o: 0.15 },
                ].map((d, i) => (
                  <circle key={i} cx={d.cx} cy={d.cy} r={d.r} fill="#FFFFFF" stroke="none" opacity={d.o} />
                ))}
              </g>
            </svg>

            {/* sparkles around the bottle */}
            {[
              { top: "6%", left: "12%", size: 26, delay: "0s", color: "#FFFFFF" },
              { top: "30%", right: "6%", size: 18, delay: "0.9s", color: "#F6DFBB" },
              { top: "62%", left: "4%", size: 20, delay: "1.6s", color: "#FFFFFF" },
              { top: "80%", right: "16%", size: 15, delay: "0.4s", color: "#F6DFBB" },
              { top: "48%", left: "20%", size: 13, delay: "2.1s", color: "#FFFFFF" },
            ].map((s, i) => (
              <svg
                key={i}
                className="absolute animate-twinkle"
                style={{ top: s.top, left: s.left, right: s.right, width: s.size, height: s.size, animationDelay: s.delay }}
                viewBox="0 0 24 24"
                fill={s.color}
              >
                <path d="M12 0C12 6 6 12 0 12C6 12 12 18 12 24C12 18 18 12 24 12C18 12 12 6 12 0Z" />
              </svg>
            ))}
          </div>
        </div>

        <div className="relative text-white shrink-0">
          <p className="font-serif text-4xl mt-4 leading-tight" data-testid="login-caption">
            Start Managing Beautifully
          </p>
        </div>
      </div>
      <div className="flex items-center justify-center px-8 py-16 bg-background">
        <div className="w-full max-w-sm animate-fade-in">
          <img src="/sterlingtask-logo.png?v=3" alt="SterlingTask" data-testid="login-app-logo" className="h-24 w-auto object-contain mb-8" />
          <div className="font-serif text-4xl tracking-tight">Welcome back</div>
          <p className="text-muted-foreground mt-3 text-sm">
            Sign in to manage your bookings and team.
          </p>
          <form onSubmit={submit} className="mt-10 space-y-6">
            <div className="space-y-2">
              <Label className="label-caps text-muted-foreground">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                placeholder="you@thesterlingtouch.com.au"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="label-caps text-muted-foreground">Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                placeholder="••••••••"
                required
              />
            </div>
            {error && (
              <p className="text-destructive text-sm" data-testid="login-error">
                {error}
              </p>
            )}
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full rounded-sm"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
            <div className="text-center">
              <Link to="/forgot-password" data-testid="forgot-password-link" className="text-sm text-muted-foreground hover:text-brand transition-colors">Forgot password?</Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
